'use client';

import {
  ChatAttachmentsPillRow,
  type ChatAttachmentPillItem,
  type UploadingFile,
} from '@terra/ui-design-system';
import type { ConversationAttachment } from '@terra/shared-contracts-conversation-attachments';

type UploadingImage = {
  id: string;
  fileName: string;
  previewUrl: string;
  progress: number;
  status: 'uploading' | 'error';
};

export type ChatAttachmentsPanelProps = {
  pendingAttachments: ConversationAttachment[];
  uploadingImages: UploadingImage[];
  uploadingFiles?: UploadingFile[];
  imagePreviewUrls: Record<string, string>;
  onRemoveUploadingImage: (uploadId: string) => void;
  onRemoveUploadingFile?: (fileId: string) => void;
  onRemoveAttachment: (attachmentId: string) => void;
  removeFileAriaLabel: string;
};

export function ChatAttachmentsPanel({
  pendingAttachments,
  uploadingImages,
  uploadingFiles = [],
  imagePreviewUrls,
  onRemoveUploadingImage,
  onRemoveUploadingFile,
  onRemoveAttachment,
  removeFileAriaLabel,
}: ChatAttachmentsPanelProps) {
  const items: ChatAttachmentPillItem[] = [
    ...uploadingImages.map((uploadingImage) => ({
      id: uploadingImage.id,
      fileName: uploadingImage.fileName,
      fileType: 'IMAGE' as const,
      previewUrl: uploadingImage.previewUrl,
      progress: uploadingImage.progress,
      status: uploadingImage.status,
    })),
    ...uploadingFiles
      .filter((file) => file.status !== 'completed')
      .map((file) => ({
        id: file.id,
        fileName: file.name,
        fileType: 'PDF' as const,
        sizeBytes: file.size,
        progress: file.progress,
        status:
          file.status === 'error'
            ? ('error' as const)
            : file.status === 'uploading'
              ? ('uploading' as const)
              : undefined,
        error: file.error,
      })),
    ...pendingAttachments.map((attachment) => ({
      id: attachment.id,
      fileName: attachment.fileName,
      fileType: attachment.fileType,
      sizeBytes: attachment.sizeBytes,
      previewUrl:
        attachment.fileType === 'IMAGE'
          ? imagePreviewUrls[attachment.id]
          : undefined,
    })),
  ];

  return (
    <ChatAttachmentsPillRow
      items={items}
      removeAriaLabel={removeFileAriaLabel}
      onRemove={(id) => {
        if (uploadingImages.some((image) => image.id === id)) {
          onRemoveUploadingImage(id);

          return;
        }

        if (uploadingFiles.some((file) => file.id === id)) {
          onRemoveUploadingFile?.(id);

          return;
        }

        onRemoveAttachment(id);
      }}
    />
  );
}
